import { useSyncExternalStore } from 'react';

/** The header the API sets on every response during a support session. */
export const IMPERSONATION_HEADER = 'x-signapass-impersonating';

export interface ImpersonationState {
  organizationId: string;
  expiresAt: string | null;
}

let current: ImpersonationState | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Reads the impersonation header off every API response — features/PLATFORM-ADMIN.md's
 * "visibly banner-flagged in the UI while active".
 *
 * ## Why the header and not an endpoint
 *
 * The dashboard could ask `/platform/me`, and then the banner would depend on
 * somebody remembering to ask on every screen. The header rides on *every*
 * response, so a screen that talks to the API at all cannot fail to know.
 *
 * The inverse matters as much: a response **without** the header clears the
 * state. A session that ended — expired, or ended from the admin surface —
 * stops showing a banner on the next request rather than at the next reload.
 */
export function readImpersonation(response: Response): void {
  const raw = response.headers.get(IMPERSONATION_HEADER);

  if (!raw) {
    if (current !== null) {
      current = null;
      emit();
    }
    return;
  }

  // `<organizationId>;expires=<iso>`
  const [organizationId, ...rest] = raw.split(';');
  const expiresAt = rest.join(';').replace(/^expires=/, '') || null;

  if (current && current.organizationId === organizationId && current.expiresAt === expiresAt) {
    return;
  }
  current = { organizationId: organizationId ?? '', expiresAt };
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => current;

/**
 * Whether this browser is currently inside somebody's account.
 *
 * `useSyncExternalStore` rather than context, because the value is written from
 * the fetch layer — outside React entirely — and every screen needs it without
 * a provider being threaded through three surfaces.
 */
export function useImpersonation(): ImpersonationState | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Test seam. Nothing in the application calls this. */
export function resetImpersonation(): void {
  current = null;
  emit();
}
